import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  createBroadcastAndReview,
  updateBroadcastAndReview,
} from 'api/broadcasts.service';
import { useToast } from 'hooks/ui';
import { Broadcast } from 'models/broadcast.model';
import { isLocalFile } from 'interfaces';
import { useApplicationStore } from 'store/application';
import { setDraftBroadcast } from 'store/broadcast';
import { broadcastRoutes } from 'router/broadcastRoutes';
import { broadcastKeys } from './queryKeys';

export const useBroadcastConditionalReviewQuery = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { setLoading, removeLoading } = useApplicationStore((state) => state);

  const { mutate, isPending } = useMutation({
    mutationFn: (broadcast: Broadcast) => {
      setLoading();

      if (broadcast.id) {
        return updateBroadcastAndReview({
          id: broadcast.id,
          title: broadcast.title,
          message: broadcast.message,
          tagIds: broadcast.tagIds,
          schedule: broadcast.schedule,
          attachments: broadcast.attachments,
          userRoles: broadcast.userRoles,
        });
      }

      return createBroadcastAndReview(
        {
          title: broadcast.title,
          message: broadcast.message,
          tagIds: broadcast.tagIds,
          schedule: broadcast.schedule?.toISOString() ?? '',
          userRoles: broadcast.userRoles,
        },
        broadcast.attachments.filter(isLocalFile),
      );
    },
    onSuccess: (broadcast) => {
      toast.success('Broadcast has been sent for review successfully');
      navigate(broadcastRoutes.broadcastId(broadcast.id));
      setDraftBroadcast(broadcast);
    },
    onError: (error: AxiosError) => toast.error(error.message),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: broadcastKeys.all });
      removeLoading();
    },
  });

  return { isLoading: isPending, mutate };
};
